// Weather Service for Pink Pixel OS
// Handles geolocation, IP location detection, weather API calls, and caching

class WeatherService {
  constructor() {
    this.baseUrl = 'https://api.openweathermap.org/data/2.5'
    this.geoUrl = 'https://api.openweathermap.org/geo/1.0'
    this.ipLocationUrl = 'https://ipapi.co/json/'
    this.cacheTimeout = 10 * 60 * 1000 // 10 minutes
    this._apiKey = null // Will be loaded dynamically
  }
  
  // Get API key dynamically to handle timing issues
  getApiKey() {
    if (!this._apiKey) {
      // Use environment variable first, then localStorage, then demo key
      this._apiKey = import.meta.env.VITE_OPENWEATHER_API_KEY || 
                     localStorage.getItem('weather_api_key') || 
                     'demo-key'
      
      // Log API key status for debugging (only once)
      if (this._apiKey === 'demo-key' || this._apiKey === 'YOUR_API_KEY_HERE' || !this._apiKey) {
        console.warn('Weather service running in demo mode - no valid API key found')
      } else {
        console.log('Weather service initialized with API key')
      }
    }
    return this._apiKey
  }
  
  // Check if we have a valid API key
  hasValidApiKey() {
    const key = this.getApiKey()
    return key && key !== 'demo-key' && key !== 'YOUR_API_KEY_HERE' && key !== 'undefined'
  }

  // Get user's location using multiple methods
  async getUserLocation() {
    try {
      // First check if we have saved location preference
      const savedLocation = this.getSavedLocation()
      if (savedLocation) {
        return savedLocation
      }

      // Try geolocation API first
      const geoLocation = await this.getGeolocation()
      if (geoLocation) {
        return geoLocation
      }

      // Fallback to IP-based location
      const ipLocation = await this.getIPLocation()
      if (ipLocation) {
        return ipLocation
      }

      // Last resort - default location
      return {
        name: 'New York',
        lat: 40.7128,
        lon: -74.0060,
        source: 'default'
      }
    } catch (error) {
      console.error('Error getting user location:', error)
      return null
    }
  }

  // Try to get location via geolocation API
  async getGeolocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'))
        return
      }

      const timeoutId = setTimeout(() => {
        reject(new Error('Geolocation timeout'))
      }, 10000) // 10 second timeout

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          clearTimeout(timeoutId)
          try {
            const { latitude, longitude } = position.coords
            
            // Get city name from coordinates using reverse geocoding
            const locationName = await this.reverseGeocode(latitude, longitude)
            
            const location = {
              name: locationName,
              lat: latitude,
              lon: longitude,
              source: 'geolocation'
            }
            
            this.saveLocationPreference(location)
            resolve(location)
          } catch (error) {
            reject(error)
          }
        },
        (error) => {
          clearTimeout(timeoutId)
          reject(new Error(`Geolocation error: ${error.message}`))
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 5 * 60 * 1000 // 5 minutes cache
        }
      )
    })
  }

  // Fallback to IP-based location
  async getIPLocation() {
    try {
      const response = await fetch(this.ipLocationUrl)
      if (!response.ok) throw new Error('IP location service unavailable')
      
      const data = await response.json()
      
      if (data.city && data.latitude && data.longitude) {
        const location = {
          name: `${data.city}, ${data.region_code || data.country}`,
          lat: parseFloat(data.latitude),
          lon: parseFloat(data.longitude),
          source: 'ip'
        }
        
        this.saveLocationPreference(location, false) // Don't override user preference
        return location
      }
      
      throw new Error('Invalid IP location data')
    } catch (error) {
      console.error('IP location error:', error)
      return null
    }
  }

  // Reverse geocode coordinates to get city name
  async reverseGeocode(lat, lon) {
    try {
      if (!this.hasValidApiKey()) {
        // Fallback name when no API key
        return 'Your Location'
      }

      const apiKey = this.getApiKey()
      const response = await fetch(
        `${this.geoUrl}/reverse?lat=${lat}&lon=${lon}&limit=1&appid=${apiKey}`
      )
      
      if (response.status === 401) {
        console.error('Invalid API key for geocoding - falling back')
        return 'Your Location'
      }
      
      if (!response.ok) throw new Error(`Geocoding failed: ${response.status}`)
      
      const data = await response.json()
      if (data && data.length > 0) {
        const place = data[0]
        return `${place.name}${place.state ? ', ' + place.state : ''}`
      }
      
      return 'Your Location'
    } catch (error) {
      console.error('Reverse geocoding error:', error)
      return 'Your Location'
    }
  }

  // Get current weather for location
  async getCurrentWeather(location) {
    try {
      // Check cache first
      const cached = this.getCachedWeather(location)
      if (cached) return cached

      if (!this.hasValidApiKey()) {
        // Return demo data when no API key or placeholder
        return this.getDemoWeatherData(location)
      }

      const apiKey = this.getApiKey()
      const response = await fetch(
        `${this.baseUrl}/weather?lat=${location.lat}&lon=${location.lon}&appid=${apiKey}&units=imperial`
      )
      
      if (response.status === 401) {
        console.error('Invalid API key - falling back to demo mode')
        return this.getDemoWeatherData(location)
      }
      
      if (!response.ok) throw new Error(`Weather API request failed: ${response.status}`)
      
      const data = await response.json()
      
      const weather = {
        location: location.name,
        temperature: Math.round(data.main.temp),
        condition: data.weather[0].main,
        description: data.weather[0].description,
        icon: data.weather[0].icon,
        humidity: data.main.humidity,
        windSpeed: Math.round(data.wind?.speed || 0),
        high: Math.round(data.main.temp_max),
        low: Math.round(data.main.temp_min),
        feelsLike: Math.round(data.main.feels_like),
        pressure: data.main.pressure,
        visibility: data.visibility ? Math.round(data.visibility / 1609.34) : null, // Convert to miles
        sunrise: new Date(data.sys.sunrise * 1000),
        sunset: new Date(data.sys.sunset * 1000),
        timestamp: Date.now()
      }

      this.cacheWeatherData(location, weather)
      return weather
    } catch (error) {
      console.error('Weather API error:', error)
      return this.getDemoWeatherData(location)
    }
  }

  // Get 5-day forecast
  async getForecast(location) {
    try {
      if (!this.hasValidApiKey()) {
        return this.getDemoForecastData()
      }

      const apiKey = this.getApiKey()
      const response = await fetch(
        `${this.baseUrl}/forecast?lat=${location.lat}&lon=${location.lon}&appid=${apiKey}&units=imperial`
      )
      
      if (response.status === 401) {
        console.error('Invalid API key for forecast - falling back to demo mode')
        return this.getDemoForecastData()
      }
      
      if (!response.ok) throw new Error(`Forecast API request failed: ${response.status}`)
      
      const data = await response.json()
      
      // Process forecast data - get one forecast per day at noon
      const dailyForecasts = []
      const processedDates = new Set()
      
      data.list.forEach(item => {
        const date = new Date(item.dt * 1000)
        const dateStr = date.toDateString()
        
        // Get the forecast closest to noon for each day
        if (!processedDates.has(dateStr) && date.getHours() >= 11 && date.getHours() <= 13) {
          processedDates.add(dateStr)
          dailyForecasts.push({
            date: date,
            temperature: Math.round(item.main.temp),
            high: Math.round(item.main.temp_max),
            low: Math.round(item.main.temp_min),
            condition: item.weather[0].main,
            description: item.weather[0].description,
            icon: item.weather[0].icon,
            humidity: item.main.humidity,
            windSpeed: Math.round(item.wind?.speed || 0)
          })
        }
      })
      
      return dailyForecasts.slice(0, 5) // Return 5 days max
    } catch (error) {
      console.error('Forecast API error:', error)
      return this.getDemoForecastData()
    }
  }

  // Search for cities by name
  async searchCities(query) {
    try {
      if (!this.hasValidApiKey()) {
        return this.getDemoCitySearch(query)
      }

      const apiKey = this.getApiKey()
      const response = await fetch(
        `${this.geoUrl}/direct?q=${encodeURIComponent(query)}&limit=5&appid=${apiKey}`
      )
      
      if (response.status === 401) {
        console.error('Invalid API key for city search - falling back to demo mode')
        return this.getDemoCitySearch(query)
      }
      
      if (!response.ok) throw new Error(`City search failed: ${response.status}`)
      
      const data = await response.json()
      
      return data.map(city => ({
        name: `${city.name}${city.state ? ', ' + city.state : ''}, ${city.country}`,
        lat: city.lat,
        lon: city.lon,
        country: city.country
      }))
    } catch (error) {
      console.error('City search error:', error)
      return this.getDemoCitySearch(query)
    }
  }

  // Save user's location preference
  saveLocationPreference(location, permanent = true) {
    try {
      const key = permanent ? 'weather_location_preference' : 'weather_location_temp'
      localStorage.setItem(key, JSON.stringify({
        ...location,
        saved: Date.now()
      }))
      
      // Dispatch custom event to notify all weather components
      window.dispatchEvent(new CustomEvent('weatherLocationChanged', {
        detail: location
      }))
    } catch (error) {
      console.error('Error saving location preference:', error)
    }
  }

  // Get saved location preference
  getSavedLocation() {
    try {
      const permanent = localStorage.getItem('weather_location_preference')
      const temp = localStorage.getItem('weather_location_temp')
      
      // Prefer permanent setting over temp
      const saved = permanent ? JSON.parse(permanent) : (temp ? JSON.parse(temp) : null)
      
      if (saved && (Date.now() - saved.saved < 24 * 60 * 60 * 1000)) { // 24 hour cache
        return {
          name: saved.name,
          lat: saved.lat,
          lon: saved.lon,
          source: saved.source
        }
      }
      
      return null
    } catch (error) {
      console.error('Error getting saved location:', error)
      return null
    }
  }

  // Cache weather data
  cacheWeatherData(location, weather) {
    try {
      const cacheKey = `weather_${location.lat}_${location.lon}`
      localStorage.setItem(cacheKey, JSON.stringify(weather))
    } catch (error) {
      console.error('Error caching weather data:', error)
    }
  }

  // Get cached weather data
  getCachedWeather(location) {
    try {
      const cacheKey = `weather_${location.lat}_${location.lon}`
      const cached = localStorage.getItem(cacheKey)
      
      if (cached) {
        const weather = JSON.parse(cached)
        if (Date.now() - weather.timestamp < this.cacheTimeout) {
          return weather
        }
      }
      
      return null
    } catch (error) {
      console.error('Error getting cached weather:', error)
      return null
    }
  }

  // Get weather icon URL
  getIconUrl(iconCode) {
    return `https://openweathermap.org/img/wn/${iconCode}@2x.png`
  }

  // Get demo weather data (for when API key not available)
  getDemoWeatherData(location) {
    const conditions = ['Sunny', 'Partly Cloudy', 'Cloudy', 'Clear']
    const temps = [65, 72, 68, 75, 70, 69, 74]
    
    const temp = temps[Math.floor(Math.random() * temps.length)]
    const condition = conditions[Math.floor(Math.random() * conditions.length)]
    
    return {
      location: location.name,
      temperature: temp,
      condition: condition,
      description: condition.toLowerCase(),
      icon: '01d', // sunny icon
      humidity: 45 + Math.floor(Math.random() * 30),
      windSpeed: Math.floor(Math.random() * 15),
      high: temp + Math.floor(Math.random() * 8),
      low: temp - Math.floor(Math.random() * 8),
      feelsLike: temp + Math.floor(Math.random() * 6) - 3,
      pressure: 1013 + Math.floor(Math.random() * 20) - 10,
      visibility: Math.floor(Math.random() * 5) + 8,
      sunrise: new Date(),
      sunset: new Date(),
      timestamp: Date.now(),
      isDemo: true
    }
  }

  // Get demo forecast data
  getDemoForecastData() {
    const conditions = ['Sunny', 'Partly Cloudy', 'Cloudy', 'Clear', 'Rain']
    const forecast = []
    
    for (let i = 1; i <= 5; i++) {
      const date = new Date()
      date.setDate(date.getDate() + i)
      
      const temp = 65 + Math.floor(Math.random() * 20)
      const condition = conditions[Math.floor(Math.random() * conditions.length)]
      
      forecast.push({
        date: date,
        temperature: temp,
        high: temp + Math.floor(Math.random() * 8),
        low: temp - Math.floor(Math.random() * 8),
        condition: condition,
        description: condition.toLowerCase(),
        icon: '01d',
        humidity: 40 + Math.floor(Math.random() * 40),
        windSpeed: Math.floor(Math.random() * 12)
      })
    }
    
    return forecast
  }

  // Demo city search
  getDemoCitySearch(query) {
    const cities = [
      { name: 'New York, NY, US', lat: 40.7128, lon: -74.0060, country: 'US' },
      { name: 'Los Angeles, CA, US', lat: 34.0522, lon: -118.2437, country: 'US' },
      { name: 'Chicago, IL, US', lat: 41.8781, lon: -87.6298, country: 'US' },
      { name: 'Houston, TX, US', lat: 29.7604, lon: -95.3698, country: 'US' },
      { name: 'Phoenix, AZ, US', lat: 33.4484, lon: -112.0740, country: 'US' }
    ]
    
    return cities.filter(city => 
      city.name.toLowerCase().includes(query.toLowerCase())
    )
  }

  // Clear all weather data
  clearCache() {
    try {
      const keys = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith('weather_')) {
          keys.push(key)
        }
      }
      keys.forEach(key => localStorage.removeItem(key))
      // Reset API key so it gets reloaded
      this._apiKey = null
    } catch (error) {
      console.error('Error clearing weather cache:', error)
    }
  }
  
  // Method to update API key and refresh
  updateApiKey(newKey) {
    if (newKey) {
      localStorage.setItem('weather_api_key', newKey)
    }
    this._apiKey = null // Force reload
    return this.getApiKey()
  }
}

// Create and export singleton instance
export const weatherService = new WeatherService()
export default weatherService
